import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Stack,
} from '@mui/material';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { getRouterPathToAuthor, JaaRoute } from '../../utilities/Links';
import { Link as RouterLink } from 'react-router';
import { Category } from '../../types/types';
import { useTranslation } from 'react-i18next';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BrandHeading from '../Page/BrandHeading';

interface AuthorResultProps {
  readonly author: string;
}

interface CategoryResultProps {
  readonly category: Category;
}

function AuthorResult({ author }: AuthorResultProps) {
  return (
    <MenuItem
      component={RouterLink}
      to={getRouterPathToAuthor(author)}
      disableGutters
    >
      <ListItemIcon>
        <PersonOutlinedIcon />
      </ListItemIcon>
      <ListItemText
        primary={author}
        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
      />
    </MenuItem>
  );
}

function CategoryResult({ category }: CategoryResultProps) {
  return (
    <MenuItem component={RouterLink} to={category.path} disableGutters>
      <ListItemIcon>
        <LabelOutlinedIcon />
      </ListItemIcon>
      <ListItemText
        primary={category.name}
        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
      />
    </MenuItem>
  );
}

interface PredictiveSearchResultsListProps {
  readonly authors: string[];
  readonly categories: Category[];
  readonly maxAuthors?: number;
}

export default function PredictiveSearchResultsList({
  authors,
  categories,
  maxAuthors = 10,
}: PredictiveSearchResultsListProps) {
  const { t } = useTranslation();

  const displayedAuthors = authors.slice(0, maxAuthors);

  return (
    <Stack
      useFlexGap
      sx={{
        gap: 3,
      }}
    >
      <MenuList
        id='predictive-search-category-result-menu'
        disablePadding
        subheader={
          categories.length > 0 && (
            <Box sx={{ paddingBottom: 2, paddingTop: 2 }}>
              <BrandHeading variant='h5'>
                {t('category.indexTitle')}
              </BrandHeading>
            </Box>
          )
        }
      >
        {categories.map((category) => {
          return (
            <CategoryResult
              key={'category-result-' + category.name}
              category={category}
            />
          );
        })}
      </MenuList>
      <MenuList
        id='predictive-search-author-result-menu'
        disablePadding
        subheader={
          authors.length > 0 && (
            <Box sx={{ paddingBottom: 2 }}>
              <BrandHeading variant='h5'>{t('authors.title')}</BrandHeading>
            </Box>
          )
        }
      >
        {displayedAuthors.map((author) => {
          return (
            <AuthorResult key={'author-result-' + author} author={author} />
          );
        })}
        {authors.length > maxAuthors && (
          <Box>
            <Button
              component={RouterLink}
              to={JaaRoute.authorListPage}
              variant='text'
              size='small'
              endIcon={<ArrowForwardIcon />}
              sx={{ margin: 1 }}
            >
              {t('author.viewAll')}
            </Button>
          </Box>
        )}
      </MenuList>
    </Stack>
  );
}
